import { ConditionalSnow, space18, BWHeader, getStyle } from "../dist/helpers";
import { useState, useEffect } from "react";
import { loginUser, getGameScore, getUserVisitCount } from "../api";
import { Link } from "react-router-dom";
import { useFormState } from "../dist/formUtils";

export default function Login(props) {
  const [snowIsEnabled] = useState(false);
  const { dark, updateLoginState, userData } = props;

  const {
    formData: { username, password },
    updateFormData,
    resetForm,
    isLoading,
    setLoading,
    message,
    messageType,
    showMessage,
  } = useFormState({ username: "", password: "" });

  const isLoggedIn = !!userData;

  const [stats, setStats] = useState({ gameScore: null, visitCount: null, loading: false });

  useEffect(() => {
    if (!userData?.username) return;
    let cancelled = false;
    setStats({ gameScore: null, visitCount: null, loading: true });
    Promise.all([
      getGameScore(userData.username),
      getUserVisitCount(userData.username),
    ]).then(([scoreResult, visitsResult]) => {
      if (cancelled) return;
      setStats({
        gameScore: scoreResult.success ? scoreResult.score : null,
        visitCount: visitsResult.success ? visitsResult.count : null,
        loading: false,
      });
    });
    return () => { cancelled = true; };
  }, [userData?.username]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    showMessage("");

    try {
      const response = await loginUser(username, password);
      if (response.success) {
        console.log('Login API response data:', response.data);
        showMessage("Login successful! Welcome back.", "success");

        const loggedInUsername = username;
        resetForm();

        let userDataToSave = { ...response.data };

        if (!userDataToSave.username) {
          userDataToSave.username = loggedInUsername;
        }

        if (!userDataToSave.id) {
          userDataToSave.id = userDataToSave.username || loggedInUsername;
        }

        updateLoginState(true, userDataToSave);
      } else {
        showMessage(response.message || "Login failed. Please check your credentials.", "error");
      }
    } catch (error) {
      console.error('Error during login:', error);
      showMessage("An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('loginState');
    updateLoginState(false, null);
  };

  const loginForm = (
    <div className="container py-4" style={{ position: "relative" }}>
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-4">
          <div
            className="h-100 p-4"
            style={getStyle(dark)}
          >
            <h2 className={`text-center mb-4 ${dark ? "text-white" : ""}`}>
              Login
            </h2>

            {message && (
              <div className={`alert alert-${messageType === "success" ? "success" : "danger"} mb-3`}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="username" className={`form-label ${dark ? "text-white" : ""}`}>
                  Username
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="username"
                  value={username}
                  onChange={(e) => updateFormData('username', e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="password" className={`form-label ${dark ? "text-white" : ""}`}>
                  Password
                </label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  value={password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Logging in..." : "Login"}
                </button>
              </div>

              <div className="text-center mt-3">
                <p className={`mb-2 ${dark ? "text-white" : "text-muted"}`}>
                  Don't have an account?
                </p>
                <Link
                  to="/signup"
                  className={`text-decoration-none ${dark ? "text-info" : "text-primary"}`}
                  style={{ fontWeight: '500' }}
                >
                  Create Account
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  const userProfile = (
    <div className="container py-4" style={{ position: "relative" }}>
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-4">
          <div
            className="h-100 p-4"
            style={getStyle(dark)}
          >
            <h2 className={`text-center mb-4 ${dark ? "text-white" : ""}`}>
              Welcome!
            </h2>

            <div className={`text-center mb-4 ${dark ? "text-white" : ""}`}>
              <h4>Hello, {userData?.username || username}!</h4>
              <p className="text-muted">You are successfully logged in.</p>
            </div>

            {/* Stats */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}>
              {[
                {
                  label: '🐍 PokeSnake Best',
                  value: stats.loading ? '…' : (stats.gameScore != null ? `${stats.gameScore} seg${stats.gameScore !== 1 ? 's' : ''}` : '—'),
                },
                {
                  label: '👁️ Total Visits',
                  value: stats.loading ? '…' : (stats.visitCount != null ? stats.visitCount : '—'),
                },
              ].map(({ label, value }) => (
                <div key={label} style={{
                  flex: 1,
                  padding: '0.75rem',
                  borderRadius: 8,
                  background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: '0.72rem', color: dark ? '#aaa' : '#888', marginBottom: '0.25rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {label}
                  </div>
                  <div style={{ fontSize: '1.3rem', fontWeight: 700, color: dark ? '#fff' : '#222' }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div className="d-grid">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      opacity: 1,
      minHeight: '100vh'
    }}>
      <center>
        {ConditionalSnow(snowIsEnabled)}
        {BWHeader(
          dark,
          isLoggedIn ? "Welcome Back" : "Login",
          isLoggedIn ? `Welcome back, ${userData?.username || username}! 🎉` : "Please enter your credentials to access your account. 🔐"
        )}
        <br />

        {isLoggedIn ? userProfile : loginForm}
        <br />

        {space18}
      </center>
    </div>
  );
}
